import { useState, useEffect } from "react";
import api from "@/lib/api";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { toast } from "sonner";
import { Save, Copy, ExternalLink, Link } from "lucide-react";

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];

export default function SettingsPage() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => toast.error(t("settings_load_error")));
  }, []);

  const updateField = (field, value) => setSettings((s) => ({ ...s, [field]: value }));
  const updateHours = (day, field, value) => {
    setSettings((s) => ({
      ...s,
      working_hours: { ...s.working_hours, [day]: { ...s.working_hours[day], [field]: value } },
    }));
  };
  const updateWidget = (field, value) => {
    setSettings((s) => ({
      ...s,
      widget_config: { ...s.widget_config, [field]: value },
    }));
  };

  const bookingUrl = `${window.location.origin}/book/${settings?.business_slug || ""}`;

  const copyBookingLink = () => {
    navigator.clipboard.writeText(bookingUrl);
    toast.success(t("settings_link_copied"));
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        business_name: settings.business_name,
        business_description: settings.business_description,
        working_hours: settings.working_hours,
        widget_config: settings.widget_config,
        business_slug: settings.business_slug,
        auto_approve_hours: settings.auto_approve_hours ?? 0,
      });
      toast.success(t("settings_saved"));
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(detail || t("settings_save_error"));
    }
    finally { setSaving(false); }
  };

  if (!settings) return <div className="text-slate-500 text-center py-12">Loading...</div>;

  return (
    <div data-testid="settings-page" className="space-y-6 animate-fade-in max-w-3xl">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold tracking-tight font-heading text-white">{t("settings_title")}</h1>
        <Button onClick={save} disabled={saving} className="bg-indigo-500 hover:bg-indigo-600" data-testid="save-settings-button">
          <Save size={16} className="mr-1" /> {saving ? t("settings_saving") : t("settings_save")}
        </Button>
      </div>

      {/* Business Info */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-4">
          <CardTitle className="text-base text-white font-heading">{t("settings_business_info")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">{t("settings_business_name")}</Label>
            <Input value={settings.business_name || ""} onChange={(e) => updateField("business_name", e.target.value)}
              className="bg-slate-800 border-slate-700 max-w-md" data-testid="business-name-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">{t("settings_business_desc")}</Label>
            <Input value={settings.business_description || ""} onChange={(e) => updateField("business_description", e.target.value)}
              placeholder={t("settings_business_desc_placeholder")} className="bg-slate-800 border-slate-700 max-w-md" data-testid="business-description-input" />
          </div>
          <div className="space-y-2">
            <Label className="text-slate-300 text-sm">{t("settings_booking_url")}</Label>
            <div className="flex items-center gap-2 max-w-md">
              <span className="text-slate-500 text-sm shrink-0">{window.location.origin}/book/</span>
              <Input
                value={settings.business_slug || ""}
                onChange={(e) => updateField("business_slug", e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "-"))}
                className="bg-slate-800 border-slate-700"
                data-testid="business-slug-input"
              />
            </div>
            <div className="flex items-center gap-2 mt-2">
              <code className="text-xs text-indigo-400 bg-slate-800 px-2 py-1 rounded border border-slate-700 truncate max-w-xs">{bookingUrl}</code>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white shrink-0" onClick={copyBookingLink} data-testid="copy-booking-link">
                <Copy size={14} />
              </Button>
              <Button variant="ghost" size="icon" className="h-7 w-7 text-slate-400 hover:text-white shrink-0" onClick={() => window.open(bookingUrl, "_blank")} data-testid="open-booking-link">
                <ExternalLink size={14} />
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Working Hours */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-4">
          <CardTitle className="text-base text-white font-heading">{t("settings_working_hours")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {DAYS.map((day) => {
            const h = settings.working_hours?.[day] || { start: "09:00", end: "17:00", enabled: false };
            return (
              <div key={day} className="flex items-center gap-4" data-testid={`hours-${day}`}>
                <div className="w-24 text-sm text-slate-300 capitalize">{day}</div>
                <Switch checked={h.enabled} onCheckedChange={(v) => updateHours(day, "enabled", v)} data-testid={`hours-toggle-${day}`} />
                <Input type="time" value={h.start} onChange={(e) => updateHours(day, "start", e.target.value)} disabled={!h.enabled}
                  className="w-32 bg-slate-800 border-slate-700 text-sm disabled:opacity-40" data-testid={`hours-start-${day}`} />
                <span className="text-slate-500 text-sm">{t("settings_hours_to")}</span>
                <Input type="time" value={h.end} onChange={(e) => updateHours(day, "end", e.target.value)} disabled={!h.enabled}
                  className="w-32 bg-slate-800 border-slate-700 text-sm disabled:opacity-40" data-testid={`hours-end-${day}`} />
              </div>
            );
          })}
        </CardContent>
      </Card>

      {/* Booking Approval */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-4">
          <CardTitle className="text-base text-white font-heading">{t("settings_approval_title")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-slate-400">{t("settings_approval_sub")}</p>
          <div className="flex items-center gap-3">
            <select
              value={settings.auto_approve_hours ?? 0}
              onChange={(e) => updateField("auto_approve_hours", Number(e.target.value) || null)}
              className="bg-slate-800 border border-slate-700 text-white text-sm rounded-lg px-3 py-2 focus:outline-none focus:border-indigo-500"
              data-testid="auto-approve-select"
            >
              <option value={0}>{t("settings_approval_off")}</option>
              <option value={0.5}>{t("settings_approval_30m")}</option>
              <option value={1}>{t("settings_approval_1h")}</option>
              <option value={2}>{t("settings_approval_2h")}</option>
              <option value={6}>{t("settings_approval_6h")}</option>
              <option value={12}>{t("settings_approval_12h")}</option>
            </select>
            {settings.auto_approve_hours ? (
              <span className="text-xs text-indigo-400">{t("settings_approval_active", { hours: settings.auto_approve_hours })}</span>
            ) : (
              <span className="text-xs text-slate-500">{t("settings_approval_inactive")}</span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Widget Config */}
      <Card className="bg-slate-900/50 border-slate-800">
        <CardHeader className="pb-4">
          <CardTitle className="text-base text-white font-heading">{t("settings_widget_appearance")}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center gap-4">
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("settings_primary_color")}</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={settings.widget_config?.primary_color || "#6366f1"}
                  onChange={(e) => updateWidget("primary_color", e.target.value)}
                  className="w-10 h-10 rounded-lg border border-slate-700 cursor-pointer bg-transparent" data-testid="widget-primary-color" />
                <span className="text-xs font-mono text-slate-500">{settings.widget_config?.primary_color || "#6366f1"}</span>
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300 text-sm">{t("settings_accent_color")}</Label>
              <div className="flex items-center gap-2">
                <input type="color" value={settings.widget_config?.accent_color || "#8b5cf6"}
                  onChange={(e) => updateWidget("accent_color", e.target.value)}
                  className="w-10 h-10 rounded-lg border border-slate-700 cursor-pointer bg-transparent" data-testid="widget-accent-color" />
                <span className="text-xs font-mono text-slate-500">{settings.widget_config?.accent_color || "#8b5cf6"}</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
